import type { BunnyPlayerApi, PlayerApiTarget } from './player-api.ts';

const PLAYERJS_CONTEXT = 'player.js';
const PLAYERJS_VERSION = '0.0.11';
const READY_EVENT = 'ready';

const SUPPORTED_EVENTS = [
  READY_EVENT,
  'play',
  'pause',
  'ended',
  'timeupdate',
  'progress',
  'seeked',
  'error',
  'playbackratechange',
] as const;

const SUPPORTED_METHODS = [
  'play',
  'pause',
  'getPaused',
  'mute',
  'unmute',
  'getMuted',
  'setVolume',
  'getVolume',
  'getDuration',
  'setCurrentTime',
  'getCurrentTime',
  'setLoop',
  'getLoop',
  'removeEventListener',
  'addEventListener',
  'setPlaybackRate',
  'getPlaybackRate',
  'playbackRate',
] as const;

type SupportedEvent = (typeof SUPPORTED_EVENTS)[number];

type PlayerJsMessage = {
  context: string;
  version?: string;
  method?: string;
  event?: string;
  value?: unknown;
  listener?: string;
};

type ReceiverWindow = Pick<
  Window,
  'parent' | 'addEventListener' | 'removeEventListener' | 'location'
>;

type ReceiverDocument = Pick<Document, 'referrer'>;

type BridgeDependencies = {
  window?: ReceiverWindow;
  document?: ReceiverDocument;
};

function parsePlayerJsMessage(data: unknown): PlayerJsMessage | null {
  let parsed = data;

  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch (_error) {
      return null;
    }
  }

  if (!parsed || typeof parsed !== 'object') return null;

  const message = parsed as PlayerJsMessage;
  if (message.context !== PLAYERJS_CONTEXT) return null;

  return message;
}

function toFiniteNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function toBoolean(value: unknown): boolean {
  return value === true || value === 'true';
}

function resolveProgressPercent(
  playerEl: PlayerApiTarget,
  duration: number,
): number {
  const buffered = (playerEl as PlayerApiTarget & { buffered?: TimeRanges })
    .buffered;

  if (!buffered || buffered.length === 0 || duration <= 0) return 0;

  try {
    const bufferedEnd = buffered.end(buffered.length - 1);
    if (!Number.isFinite(bufferedEnd)) return 0;
    return Math.max(0, Math.min((bufferedEnd / duration) * 100, 100));
  } catch (_error) {
    return 0;
  }
}

export class BunnyPlayerJsReceiver {
  readonly #playerEl: PlayerApiTarget;
  readonly #playerApi: BunnyPlayerApi;
  readonly #window: ReceiverWindow;
  readonly #document: ReceiverDocument;
  readonly #eventListeners = new Map<string, Set<string>>();
  readonly #mediaEventBindings = new Map<string, EventListener>();
  readonly #messageHandler: (event: MessageEvent) => void;
  #connected = false;
  #ready = false;
  #reject = false;
  #origin = '*';

  constructor(
    playerEl: PlayerApiTarget,
    playerApi: BunnyPlayerApi,
    dependencies: BridgeDependencies = {},
  ) {
    this.#playerEl = playerEl;
    this.#playerApi = playerApi;
    this.#window = dependencies.window ?? window;
    this.#document = dependencies.document ?? document;
    this.#messageHandler = (event: MessageEvent) => {
      this.#receiveMessage(event);
    };
  }

  connect(): void {
    if (this.#connected) return;

    this.#connected = true;
    this.#reject = this.#window.parent === this.#window;
    this.#origin = this.#resolveOrigin();

    this.#window.addEventListener('message', this.#messageHandler);
    this.#bindPlayerEvents();

    if (!this.#reject) {
      queueMicrotask(() => this.ready());
    }
  }

  disconnect(): void {
    if (!this.#connected) return;

    this.#window.removeEventListener('message', this.#messageHandler);
    for (const [eventName, listener] of this.#mediaEventBindings) {
      this.#playerEl.removeEventListener(eventName, listener);
    }
    this.#mediaEventBindings.clear();
    this.#eventListeners.clear();
    this.#connected = false;
  }

  ready(): void {
    if (this.#reject) return;

    this.#ready = true;
    this.#sendEvent(READY_EVENT, {
      src: `${this.#window.location}`,
      events: [...SUPPORTED_EVENTS],
      methods: [...SUPPORTED_METHODS],
    });
  }

  #resolveOrigin(): string {
    try {
      if (!this.#document.referrer) return '*';
      return new URL(this.#document.referrer).origin;
    } catch (_error) {
      return '*';
    }
  }

  #shouldAcceptMessage(event: MessageEvent): boolean {
    if (event.source !== this.#window.parent) return false;
    if (this.#origin === '*') return true;

    return Boolean(event.origin) && event.origin !== 'null' &&
      event.origin === this.#origin;
  }

  #sendEvent(event: string, value?: unknown, listener?: string): void {
    if (this.#reject) return;

    const payload: PlayerJsMessage = {
      context: PLAYERJS_CONTEXT,
      version: PLAYERJS_VERSION,
      event,
    };

    if (value !== undefined) payload.value = value;
    if (listener) payload.listener = listener;

    this.#window.parent.postMessage(JSON.stringify(payload), this.#origin);
  }

  #sendError(code: number, msg: string, listener?: string): void {
    this.#sendEvent('error', { code, msg }, listener);
  }

  #bindPlayerEvents(): void {
    const eventBindings: Array<[string, EventListener]> = [
      ['play', () => this.#emitEventIfSubscribed('play')],
      ['pause', () => this.#emitEventIfSubscribed('pause')],
      ['ended', () => this.#emitEventIfSubscribed('ended')],
      ['seeked', () => this.#emitEventIfSubscribed('seeked')],
      ['error', () => this.#emitEventIfSubscribed('error')],
      [
        'ratechange',
        () =>
          this.#emitEventIfSubscribed(
            'playbackratechange',
            this.#playerApi.getPlaybackRate(),
          ),
      ],
      [
        'timeupdate',
        () => this.#emitEventIfSubscribed('timeupdate', this.#getTimePayload()),
      ],
      [
        'progress',
        () =>
          this.#emitEventIfSubscribed('progress', this.#getProgressPayload()),
      ],
    ];

    for (const [eventName, listener] of eventBindings) {
      this.#playerEl.addEventListener(eventName, listener);
      this.#mediaEventBindings.set(eventName, listener);
    }
  }

  #getTimePayload(): { seconds: number; duration: number } {
    return {
      seconds: this.#playerApi.getCurrentTime(),
      duration: this.#playerApi.getDuration(),
    };
  }

  #getProgressPayload(): {
    percent: number;
    seconds: number;
    duration: number;
  } {
    const duration = this.#playerApi.getDuration();

    return {
      percent: resolveProgressPercent(this.#playerEl, duration),
      seconds: this.#playerApi.getCurrentTime(),
      duration,
    };
  }

  #emitEventIfSubscribed(eventName: SupportedEvent, value?: unknown): void {
    const listeners = this.#eventListeners.get(eventName);
    if (!listeners || listeners.size === 0) return;

    for (const listener of listeners) {
      this.#sendEvent(eventName, value, listener);
    }
  }

  #receiveMessage(event: MessageEvent): void {
    const message = parsePlayerJsMessage(event.data);
    if (!message?.method) return;
    if (!this.#shouldAcceptMessage(event)) return;

    switch (message.method) {
      case 'addEventListener':
        this.#addEventListener(message.value, message.listener);
        return;
      case 'removeEventListener':
        this.#removeEventListener(message.value, message.listener);
        return;
      default:
        void this.#invokeMethod(
          message.method,
          message.value,
          message.listener,
        );
    }
  }

  #addEventListener(value: unknown, listener?: string): void {
    if (typeof value !== 'string' || !listener) return;

    const listeners = this.#eventListeners.get(value) ?? new Set<string>();
    listeners.add(listener);
    this.#eventListeners.set(value, listeners);

    if (value === READY_EVENT && this.#ready) {
      this.ready();
    }
  }

  #removeEventListener(value: unknown, listener?: string): void {
    if (typeof value !== 'string' || !listener) return;

    const listeners = this.#eventListeners.get(value);
    if (!listeners) return;

    listeners.delete(listener);
    if (listeners.size === 0) {
      this.#eventListeners.delete(value);
    }
  }

  async #invokeMethod(
    method: string,
    value: unknown,
    listener?: string,
  ): Promise<void> {
    if (
      !SUPPORTED_METHODS.includes(method as (typeof SUPPORTED_METHODS)[number])
    ) {
      this.#sendError(2, `Invalid Method "${method}"`, listener);
      return;
    }

    try {
      switch (method) {
        case 'play':
          await Promise.resolve(this.#playerApi.play());
          return;
        case 'pause':
          this.#playerApi.pause();
          return;
        case 'getPaused':
          this.#sendEvent(method, this.#playerApi.getPaused(), listener);
          return;
        case 'mute':
          this.#playerApi.mute();
          return;
        case 'unmute':
          this.#playerApi.unmute();
          return;
        case 'getMuted':
          this.#sendEvent(method, this.#playerApi.getMuted(), listener);
          return;
        case 'setVolume':
          this.#playerApi.setVolume(toFiniteNumber(value, 100));
          return;
        case 'getVolume':
          this.#sendEvent(method, this.#playerApi.getVolume(), listener);
          return;
        case 'getDuration':
          this.#sendEvent(method, this.#playerApi.getDuration(), listener);
          return;
        case 'setCurrentTime':
          this.#playerApi.setCurrentTime(toFiniteNumber(value, 0));
          return;
        case 'getCurrentTime':
          this.#sendEvent(method, this.#playerApi.getCurrentTime(), listener);
          return;
        case 'setLoop':
          this.#playerApi.setLoop(toBoolean(value));
          return;
        case 'getLoop':
          this.#sendEvent(method, this.#playerApi.getLoop(), listener);
          return;
        case 'setPlaybackRate':
          this.#playerApi.setPlaybackRate(toFiniteNumber(value, 1));
          return;
        case 'getPlaybackRate':
          this.#sendEvent(method, this.#playerApi.getPlaybackRate(), listener);
          return;
        case 'playbackRate':
          if (value !== undefined) {
            this.#playerApi.setPlaybackRate(toFiniteNumber(value, 1));
            return;
          }
          this.#sendEvent(method, this.#playerApi.getPlaybackRate(), listener);
          return;
        default:
          this.#sendError(3, `Method Not Supported "${method}"`, listener);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.#sendError(5, message, listener);
    }
  }
}

export const playerJsSupportedEvents = [...SUPPORTED_EVENTS];
export const playerJsSupportedMethods = [...SUPPORTED_METHODS];
